import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, MessageSquare, Plus, UserPlus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ActionBar } from '../components/ActionBar';
import { AssignPanel } from '../components/AssignPanel';
import { HelpHint } from '../components/HelpHint';
import { IncidentPanel } from '../components/IncidentPanel';
import { ManageTicket } from '../components/ManageTicket';
import { TicketPhotos } from '../components/PhotoGallery';
import { TechnicianReport } from '../components/TechnicianReport';
import { SlaBadge, SlaBar } from '../components/SlaBadge';
import { PriorityBadge, StatusBadge } from '../components/StatusBadge';
import { PageNav } from '../components/PageNav';
import { api } from '../lib/api';
import { useSession } from '../lib/session';

/**
 * A note from either side, landing on the timeline.
 *
 * Deliberately not a chat window: a message thread beside a ticket becomes the
 * place decisions are made and then scrolls away. On the timeline it sits next
 * to the status change it explains, and cannot be edited afterwards.
 */
function NoteBox({ jobId }: { jobId: string }) {
  const queryClient = useQueryClient();
  const [body, setBody] = useState('');

  const add = useMutation({
    mutationFn: () => api.addNote(jobId, body),
    onSuccess: async () => {
      setBody('');
      await queryClient.invalidateQueries({ queryKey: ['ticket', jobId] });
    },
  });

  return (
    <form
      className="mb-4 flex gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (body.trim()) add.mutate();
      }}
    >
      <input
        className="input flex-1"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Add a note for whoever reads this next"
        maxLength={2000}
      />
      <button type="submit" className="btn-secondary" disabled={!body.trim() || add.isPending}>
        {add.isPending ? 'Adding…' : 'Add'}
      </button>
    </form>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  if (!value) return null;
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm">{value}</dd>
    </div>
  );
}

export function TicketDetailPage() {
  const { id = '' } = useParams();
  const { can } = useSession();
  const queryClient = useQueryClient();
  const [watcherOpen, setWatcherOpen] = useState(false);
  const [watcher, setWatcher] = useState({ name: '', address: '', relationship: 'supervisor' });

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['ticket', id],
    queryFn: () => api.ticket(id),
    refetchInterval: 15_000,
  });

  /*
   * Land on the panel the link promised.
   *
   * React Router does not scroll to a hash of its own accord, and nothing else
   * here does either - so `/tickets/<id>#assign` from the list would open the
   * ticket at the top with the Assign card still below the fold. That is the
   * same "there is nowhere to assign from" it was added to answer, moved one
   * step along, which is worse than not adding it: the link would look like it
   * had failed.
   *
   * Waits for `data`, because the panel does not exist until the ticket has
   * loaded. Depends on the hash too, so clicking the link again from within
   * the same ticket scrolls again rather than doing nothing.
   */
  const { hash } = useLocation();
  useEffect(() => {
    if (!data || !hash) return;
    const target = document.querySelector(hash);
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [data, hash]);

  const addWatcher = useMutation({
    mutationFn: () =>
      api.addWatcher(id, { ...watcher, channel: 'email', notifyOn: ['assigned', 'completed'] }),
    onSuccess: async () => {
      setWatcherOpen(false);
      setWatcher({ name: '', address: '', relationship: 'supervisor' });
      await queryClient.invalidateQueries({ queryKey: ['ticket', id] });
    },
  });

  if (isPending) {
    return <Loader2 className="mx-auto mt-16 h-6 w-6 animate-spin text-primary-500" />;
  }

  if (isError) {
    return (
      <div className="mx-auto max-w-md py-16 text-center">
        <p className="text-danger-600">{(error as Error).message}</p>
        <Link to="/" className="btn-secondary mt-4">
          Back to tickets
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <PageNav
        backTo="/list"
        backLabel="Back to tickets"
        crumbs={[{ label: 'Tickets', to: '/list' }, { label: data.reference }]}
      />

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-heading text-2xl">{data.reference}</h2>
            <StatusBadge status={data.status} />
            <PriorityBadge priority={data.priority} />
            <SlaBadge
              state={data.slaState}
              percent={data.slaPercent}
              targetMin={data.slaTargetMin}
            />
          </div>
          <p className="mt-1 text-muted-foreground">{data.title}</p>
          {/* The clock, where the eye already is. A number nobody has to open
              a panel to find is a number people actually act on. */}
          {data.slaState && data.slaState !== 'PAUSED' && (
            <div className="mt-2 max-w-xs">
              <SlaBar state={data.slaState} percent={data.slaPercent} />
            </div>
          )}
        </div>
      </div>

      {data.onHoldReason && (
        <div className="rounded-xl bg-accent-50 p-3 text-sm text-accent-600">
          On hold — {data.onHoldReason}. SLA clocks are paused.
        </div>
      )}

      {data.parentIncident && (
        <div className="rounded-xl bg-muted p-3 text-sm">
          Linked to incident{' '}
          <Link
            to={`/tickets/${data.parentIncident.id}`}
            className="font-semibold text-primary-600 hover:underline"
          >
            {data.parentIncident.reference}
          </Link>{' '}
          — no technician is dispatched for this ticket separately.
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <section className="card">
            <h3 className="mb-4 font-heading text-lg">Details</h3>
            <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <Field label="Job type" value={data.jobType.name} />
              <Field
                label="Area"
                value={data.area && `${data.area.name} · ${data.area.zone.code}`}
              />
              <Field label="Customer" value={data.customer?.name} />
              <Field label="Phone" value={data.customer?.phoneE164} />
              <Field label="Alternative phone" value={data.customer?.altPhoneE164} />
              <Field label="House / plot" value={data.site?.houseNo} />
              <Field label="Landmark" value={data.site?.landmark} />
              <Field
                label="Network element"
                value={data.element && `${data.element.name} (${data.element.status})`}
              />
              <Field label="Root cause" value={data.rootCauseCode} />
              <Field label="Outcome" value={data.outcome} />
            </dl>
            {data.site?.accessNotes && (
              <p className="mt-4 rounded-xl bg-accent-50 p-3 text-sm text-accent-600">
                Access: {data.site.accessNotes}
              </p>
            )}
          </section>

          <ManageTicket
            ticketId={data.id}
            current={{
              title: data.title,
              description: data.description,
              priority: data.priority,
              status: data.status,
              estDurationMin: data.estDurationMin ?? null,
              areaId: data.area?.id ?? null,
              customerId: data.customer?.id ?? null,
              archivedAt: data.archivedAt ?? null,
              archivedReason: data.archivedReason ?? null,
            }}
          />

          <TechnicianReport
            ticketId={data.id}
            status={data.status}
            outcome={data.outcome}
            report={data.report}
            materials={data.materials ?? []}
            materialsOther={data.materialsOther ?? null}
            completionData={data.completionData ?? null}
            assignee={data.assignee}
          />

          <TicketPhotos ticketId={data.id} />

          <IncidentPanel
            ticketId={data.id}
            isIncident={data.isIncident}
            parentIncident={data.parentIncident}
            childCount={data.children.length}
            status={data.status}
          />

          {data.children.length > 0 && (
            <section className="card">
              <h3 className="mb-3 flex items-center gap-1.5 font-heading text-lg">
                Linked tickets ({data.children.length})
                <HelpHint>
                  Customer tickets attached to this incident. Resolving the incident resolves them
                  together, with one notice each.
                </HelpHint>
              </h3>
              <ul className="divide-y divide-border text-sm">
                {data.children.map((child) => (
                  <li key={child.id} className="flex items-center justify-between py-2">
                    <Link to={`/tickets/${child.id}`} className="text-primary-600 hover:underline">
                      {child.reference} — {child.title}
                    </Link>
                    <StatusBadge status={child.status} />
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="card">
            <h3 className="mb-3 flex items-center gap-1.5 font-heading text-lg">
              <MessageSquare className="h-4 w-4" aria-hidden="true" />
              Messages
              <HelpHint>
                Every delivery attempt, on every channel. This is what answers &quot;did he get
                it?&quot; without anyone guessing.
              </HelpHint>
            </h3>
            {data.messages.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Nothing sent yet — assign a technician to notify them.
              </p>
            ) : (
              <ul className="space-y-3">
                {data.messages.map((message) => (
                  <li key={message.id} className="rounded-xl bg-muted/50 p-3 text-sm">
                    <div className="mb-1.5 flex flex-wrap items-center gap-2 text-xs">
                      <span className="badge bg-primary-50 text-primary-700">
                        {message.channel}
                      </span>
                      <span className="text-muted-foreground">→ {message.toAddress}</span>
                      <span
                        className={`badge ${
                          message.status === 'FAILED'
                            ? 'bg-danger-50 text-danger-600'
                            : 'bg-secondary-50 text-secondary-700'
                        }`}
                      >
                        {message.status.toLowerCase()}
                      </span>
                    </div>
                    <pre className="whitespace-pre-wrap font-body text-sm">
                      {message.renderedText}
                    </pre>
                    {message.body.buttons && message.body.buttons.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {message.body.buttons.map((button) => (
                          <span
                            key={button}
                            className="rounded-lg border border-border bg-card px-3 py-1 text-xs font-medium"
                          >
                            {button}
                          </span>
                        ))}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="card">
            <h3 className="mb-3 font-heading text-lg">Timeline</h3>
            {can('job:note') && <NoteBox jobId={data.id} />}
            <ol className="space-y-3 text-sm">
              {data.events.map((event) => (
                <li key={event.id} className="flex gap-3">
                  <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary-300" />
                  <div className="min-w-0">
                    {event.kind === 'note' ? (
                      <p className="whitespace-pre-wrap rounded-xl bg-muted/50 px-3 py-2">
                        {(event.payload as { body?: string } | null)?.body}
                      </p>
                    ) : (
                      <p>
                        {event.toStatus ? (
                          <>
                            {event.fromStatus ? `${event.fromStatus} → ` : ''}
                            <strong>{event.toStatus}</strong>
                          </>
                        ) : (
                          event.kind.replace(/_/g, ' ')
                        )}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {new Date(event.occurredAt).toLocaleString('en-KE')} ·{' '}
                      {event.actorLabel ?? 'system'}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        </div>

        <div className="space-y-5">
          <ActionBar jobId={data.id} version={data.version} />

          {can('ticket:assign') && (
            <AssignPanel
              jobId={data.id}
              areaId={data.area?.id ?? null}
              areaName={data.area?.name ?? null}
              requiredSkills={data.jobType.requiredSkills}
              currentAssigneeId={data.assignee?.id}
            />
          )}

          <section className="card">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="flex items-center gap-1.5 font-heading text-lg">
                Followers
                <HelpHint>
                  People kept informed but not doing the work. They get a read-only link — never the
                  technician&apos;s number.
                </HelpHint>
              </h3>
              {can('ticket:add_watcher') && data.watchers.length < 5 && (
                <button
                  type="button"
                  onClick={() => setWatcherOpen((v) => !v)}
                  className="inline-flex items-center gap-1 text-sm font-semibold text-primary-600 hover:underline"
                >
                  <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                  Add
                </button>
              )}
            </div>

            {data.watchers.length === 0 && !watcherOpen && (
              <p className="text-sm text-muted-foreground">
                Nobody is following this ticket yet.
              </p>
            )}

            <ul className="divide-y divide-border">
              {data.watchers.map((w) => (
                <li key={w.id} className="py-2.5 text-sm">
                  <div className="flex items-center gap-2">
                    <UserPlus className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                    <span className="font-medium">{w.name}</span>
                    <span className="badge bg-muted text-muted-foreground">{w.relationship}</span>
                  </div>
                  <p className="mt-0.5 pl-5 text-xs text-muted-foreground">
                    {w.channel} · {w.address} · notified on {w.notifyOn.join(', ')}
                  </p>
                </li>
              ))}
            </ul>

            {watcherOpen && (
              <div className="mt-3 space-y-3 border-t border-border pt-3">
                <input
                  value={watcher.name}
                  onChange={(e) => setWatcher({ ...watcher, name: e.target.value })}
                  placeholder="Name"
                  aria-label="Follower name"
                  className="input-field"
                />
                <input
                  value={watcher.address}
                  onChange={(e) => setWatcher({ ...watcher, address: e.target.value })}
                  placeholder="Email address"
                  aria-label="Follower email"
                  className="input-field"
                />
                <select
                  value={watcher.relationship}
                  onChange={(e) => setWatcher({ ...watcher, relationship: e.target.value })}
                  aria-label="Relationship"
                  className="input-field"
                >
                  {['supervisor', 'client_contact', 'contractor', 'landlord', 'vendor', 'other'].map(
                    (r) => (
                      <option key={r} value={r}>
                        {r.replace(/_/g, ' ')}
                      </option>
                    ),
                  )}
                </select>
                {addWatcher.isError && (
                  <p className="text-sm text-danger-600">{(addWatcher.error as Error).message}</p>
                )}
                <button
                  type="button"
                  onClick={() => addWatcher.mutate()}
                  disabled={!watcher.name || !watcher.address || addWatcher.isPending}
                  className="btn-primary w-full"
                >
                  Add follower
                </button>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
